import React, { useState } from 'react'
import { Card, CardContent, Dialog, DialogContent, Icon, Stack, Typography } from '@mui/material'
import { CardTitle } from '../../../components/card/NewCard'
import { DefaultDialogTitle } from '../../../components/styled/Dialog'
import Select from '../../../components/forms/Select'
import Table from '../../../components/datatable/Table'
import { darkBlue } from '../../../utils/constants/ColorTheme'
import { INDICATOR_TYPE, getIndicatorTypes } from '../../constants/IndicatorsConstants'

const WATER_STRESS_EXAMPLE = [
    { element: 'Enveloppe min de la semaine', valeur: '12,40 m NGF', role: 'Vaut 0 / 100' },
    { element: 'Enveloppe max de la semaine', valeur: '15,20 m NGF', role: 'Vaut 100 / 100' },
    { element: 'Mesure du jour (max journalier)', valeur: '13,80 m NGF', role: 'À positionner entre les deux' },
    { element: 'Score obtenu', valeur: '50 / 100', role: '100 x (13,80 - 12,40) / (15,20 - 12,40)' },
]

const WaterStressExplanation = () => (
    <Stack spacing={1}>
        <Card>
            <CardTitle color={darkBlue} title={'Ce que mesure l\'indicateur'} />
            <CardContent>
                <Typography>
                    Le stress hydrique situe le niveau du jour par rapport à ce que la ressource a historiquement connu
                    à la même période de l'année. Le score va de 0 (niveau le plus bas jamais observé cette semaine-là)
                    à 100 (niveau le plus haut jamais observé cette semaine-là). Un score bas signale donc une ressource
                    inhabituellement basse pour la saison, et non un niveau bas dans l'absolu.
                </Typography>
            </CardContent>
        </Card>
        <Card>
            <CardTitle color={darkBlue} title={'Comment il est calculé'} />
            <CardContent>
                <Typography>
                    Le calcul se fait par station, jour par jour, et uniquement sur les données validées. Pour chaque
                    jour, on retient le maximum journalier, puis on le compare aux enveloppes min et max de la semaine
                    correspondante de l'année, calculées sur tout l'historique de la station :
                </Typography>
                <Typography className='padding-top-1'>
                    <b>Score = 100 x (mesure - enveloppe min) / (enveloppe max - enveloppe min)</b>
                </Typography>
                <Typography className='padding-top-1'>
                    Le résultat est borné entre 0 et 100 : une mesure qui bat le record bas de la semaine reste à 0, une
                    mesure qui bat le record haut reste à 100. Le calcul est mené en cote NGF, ce qui le rend identique
                    en piézométrie et en hydrométrie : dans les deux cas une valeur haute correspond à une ressource
                    haute. Sur une station piézométrique, une profondeur faible donne donc bien un score élevé.
                </Typography>
            </CardContent>
        </Card>
        <Card>
            <CardTitle color={darkBlue} title={'Exemple'} />
            <CardContent>
                <Typography>
                    Sur un piézomètre, en semaine 36, l'historique montre un niveau qui a varié entre 12,40 et 15,20 m
                    NGF à cette période de l'année. Le maximum du jour est de 13,80 m NGF :
                </Typography>
                <Table
                    showTitle={false}
                    data={WATER_STRESS_EXAMPLE}
                    type={{ headers: ['element', 'valeur', 'role'] }}
                    customHeaders={{ element: 'Élément', valeur: 'Valeur', role: 'Rôle dans le calcul' }}
                    condensed
                    sortable={false}
                />
            </CardContent>
        </Card>
        <Card>
            <CardTitle color={darkBlue} title={'Regroupement des stations'} />
            <CardContent>
                <Typography>
                    Une ressource vaut la moyenne des scores de ses stations de surveillance, jour par jour. Une unité
                    de production vaut la moyenne des scores des ressources qu'elle exploite. Une station sans donnée un
                    jour donné est simplement ignorée ce jour-là : la moyenne se fait sur les stations disponibles. Si
                    aucune station n'a de donnée, aucun indicateur n'est produit pour cette journée.
                </Typography>
            </CardContent>
        </Card>
        <Card>
            <CardTitle color={darkBlue} title={'Ce qui empêche le calcul'} />
            <CardContent>
                <Typography>
                    Aucun score n'est produit si la station n'a pas assez d'historique pour que les enveloppes min et max
                    de la semaine soient différentes l'une de l'autre — le rapport serait alors une division par zéro.
                    C'est typiquement le cas d'une station mise en service depuis moins d'un an.
                </Typography>
            </CardContent>
        </Card>
    </Stack>
)

const IndicatorsExplanationDialog = () => {
    const [open, setIsOpen] = useState(false)
    const [indicatorType, setIndicatorType] = useState(INDICATOR_TYPE.WATER_STRESS)

    return (
        <>
            <Icon
                style={{ fontSize: 14, position: 'relative', top: '-49px', left: '125px', cursor: 'pointer' }}
                tooltip={'Explication des calculs d\'indicateurs'}
                onClick={() => setIsOpen(true)}
            >help</Icon>
            <Dialog open={open} maxWidth='md' fullWidth>
                <DefaultDialogTitle title={'Calcul des indicateurs'} onClose={() => setIsOpen(false)} />
                <DialogContent>
                    <Stack spacing={1} className='padding-top-1'>
                        <Select
                            label={'Indicateur'}
                            value={indicatorType}
                            options={getIndicatorTypes()}
                            onChange={setIndicatorType}
                            noNullValue
                            integerValue
                        />
                        {indicatorType === INDICATOR_TYPE.WATER_STRESS && <WaterStressExplanation />}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default IndicatorsExplanationDialog